<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IDownloadReportListItem } from '@modules/download-report/types';
import type { ITableColumnsOptions, ITableMultiselectActions } from '@modules/tables/types';
import VCard from '@components/card/VCard/VCard.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import { useRestClient } from '@modules/core/services/api';
import {
  DOWNLOAD_REPORT_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_DOWNLOAD_REPORT,
  TRANSLATION_GROUP_AFFILIATE_DOWNLOAD_REPORT,
} from '@modules/download-report/constants';
import {
  useDownloadReportDeleteMutation,
  useDownloadReportLinkMutation,
} from '@modules/download-report/mutations';
import { useDownloadReportListQuery } from '@modules/download-report/queries';
import { EDownloadReportTypes } from '@modules/download-report/types';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';
import { useTableData } from '@modules/tables/composables';
import { useUserSupervision } from '@modules/user/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useDownloadReportListQuery);

const reportFormat = ref(EDownloadReportTypes.CSV);

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();
const { openLink } = useRestClient();
const { hasPermission } = usePermissionsStore();
const { isSupervision } = useUserSupervision();

const { mutateAsync: getReportLink } = useDownloadReportLinkMutation();
const { mutateAsync: deleteReport } = useDownloadReportDeleteMutation();

const { isAdminNamespace } = useCurrentNamespace();

const columnOptions = computed<ITableColumnsOptions>(() => ({
  aasm_state: {
    localization: {
      translationPath: isAdminNamespace.value ? 'admin.export.download_csv.aasm_state.' : 'affiliate.export.download_csv.aasm_state.',
    },
  },
  report_file_name: {
    defaultMobilePreview: true,
  },
}));

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_DOWNLOAD_REPORT : TRANSLATION_GROUP_AFFILIATE_DOWNLOAD_REPORT
));

const tableActions = computed<ITableMultiselectActions>(() => {
  const actions: ITableMultiselectActions = {};

  if (hasPermission(DOWNLOAD_REPORT_PERMISSIONS.SHOW).value) {
    actions.downloadReportCSV = {
      label: getTranslation(translationGroup.value, 'DOWNLOAD_REPORT_CSV').value,
      icon: 'pi-download',
    };
    actions.downloadReportXLSX = {
      label: getTranslation(translationGroup.value, 'DOWNLOAD_REPORT_XLSX').value,
      icon: 'pi-file-excel',
    };
  }

  if (hasPermission(DOWNLOAD_REPORT_PERMISSIONS.EDIT).value && !isSupervision.value) {
    actions.removeReport = {
      label: getTranslation(translationGroup.value, 'REMOVE_REPORT').value,
      icon: 'pi-trash',
    };
  }

  return actions;
});

function onTableActionItemSelect(optionId: string, listItem?: IDownloadReportListItem): void {
  if (!listItem) {
    throw new Error('No list item provided!');
  }

  switch (optionId) {
    case 'downloadReportCSV':
      reportFormat.value = EDownloadReportTypes.CSV;
      void onDownloadClick(listItem);
      break;
    case 'downloadReportXLSX':
      reportFormat.value = EDownloadReportTypes.XLSX;
      void onDownloadClick(listItem);
      break;
    case 'removeReport':
      onDelete(listItem);
      break;
    default:
      break;
  }
}

async function onDownloadClick({ id }: IDownloadReportListItem): Promise<void> {
  const { link } = await getReportLink({ id, format: reportFormat.value });
  openLink(link, { excludeHost: true });
}

function onDelete({ id, report_file_name }: IDownloadReportListItem): void {
  confirmDelete({
    group: 'confirmDialog',
    header: getTranslation(translationGroup.value, 'MODAL_TITLE').value,
    message: getTranslation(translationGroup.value, 'MODAL_QUESTION', { report_file_name }).value,
    acceptLabel: getTranslation(translationGroup.value, 'MODAL_YES').value,
    rejectLabel: getTranslation(translationGroup.value, 'MODAL_NO').value,
    accept: () => {
      void deleteReport(id);
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'DownloadReportListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(translationGroup, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VTableMultiselectActions
          :list-item="listItem"
          :options="tableActions"
          :disabled="listItem.aasm_state !== EAasmState.READY"
          static
          @select="onTableActionItemSelect"
        />
      </template>
    </VTable>
  </VCard>
</template>
